import { toast } from "sonner";
import { LogOut, RotateCcw, Trash2, UserCheck, UserX } from "lucide-react";
import { http } from "@/lib/api";
import { useStaff } from "@/lib/staff";
import { Card } from "@/components/staff/ui";
import { ConfirmAction } from "@/components/staff/ConfirmAction";
import { FeaturesAction, GivePass, GivePlan, LimitsAction, RemovePlan } from "@/components/staff/StudentActions";

const post = (uid, path, body) => http.post(`/staff/students/${uid}/${path}`, body);

function Group({ label, children }) {
  const items = [children].flat().filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</p>
      <div className="flex flex-wrap gap-2">{items}</div>
    </div>
  );
}

function AccountActions({ d, onDone }) {
  const { can } = useStaff();
  const uid = d.profile.user_id;
  const { status } = d.profile;
  return (
    <Group label="Account">
      {can("students.signout") && (
        <ConfirmAction key="signout" label="Sign out all devices" icon={LogOut} testid="action-signout" title="Sign out every device?" confirmLabel="Sign out"
          description="Every session ends straight away. The student can sign in again."
          run={async (reason) => { await post(uid, "signout", { reason }); toast.success("Signed out everywhere"); onDone(); }} />
      )}
      {can("students.suspend") && status !== "suspended" && (
        <ConfirmAction key="suspend" label="Suspend" icon={UserX} danger testid="action-suspend" title="Suspend this account?" confirmLabel="Suspend account"
          description="They're signed out everywhere and can't sign in. A paid plan stops renewing (no refund). Restoring later won't restart renewal."
          run={async (reason) => {
            const { data } = await post(uid, "suspend", { reason });
            if (data.warning) toast.warning(data.warning); else toast.success("Account suspended");
            onDone();
          }} />
      )}
      {can("students.suspend") && status === "suspended" && (
        <ConfirmAction key="restore" label="Restore account" icon={UserCheck} testid="action-unsuspend" title="Restore this account?" confirmLabel="Restore"
          description="They can sign in again. Their plan won't start renewing again on its own."
          run={async (reason) => { await post(uid, "unsuspend", { reason }); toast.success("Account restored"); onDone(); }} />
      )}
      {can("students.delete") && !d.profile.deletion && (
        <ConfirmAction key="delete" label="Schedule deletion" icon={Trash2} danger needsVerify testid="action-delete" title="Schedule this account for deletion?" confirmLabel="Schedule deletion"
          description="Everything in the account is deleted in 14 days unless it's restored. Their plan stops renewing now (no refund) and they're signed out everywhere."
          run={async (reason, token) => { await post(uid, "delete", { reason, reauth_token: token }); toast.success("Deletion scheduled"); onDone(); }} />
      )}
      {can("students.delete") && d.profile.deletion && (
        <ConfirmAction key="cancel-delete" label="Cancel deletion" icon={RotateCcw} testid="action-cancel-delete" title="Cancel the scheduled deletion?" confirmLabel="Keep the account"
          description="The account stays. If deletion stopped their renewal, it switches back on (unless the account is suspended)."
          run={async (reason) => { await post(uid, "delete/cancel", { reason }); toast.success("Deletion cancelled"); onDone(); }} />
      )}
    </Group>
  );
}

export function StudentActionsPanel({ d, onDone }) {
  const { can } = useStaff();
  const uid = d.profile.user_id;
  const comp = d.profile.comp_plan;
  if (!d.can_act) return (
    <Card title="Actions" testid="student-actions">
      <p className="text-sm text-neutral-500" data-testid="student-actions-locked">You can't change this account here. It's your own, the owner's, or a staff member at or above your role.</p>
    </Card>
  );
  return (
    <Card title="Actions" testid="student-actions">
      <div className="space-y-5">
        <Group label="Plans and passes">
          {can("plans.grant") && <GivePlan key="plan" uid={uid} onDone={onDone} />}
          {can("plans.grant") && comp && <RemovePlan key="remove-plan" uid={uid} comp={comp} onDone={onDone} />}
          {can("plans.grant") && <GivePass key="pass" uid={uid} onDone={onDone} />}
        </Group>
        <Group label="Limits and features">
          {can("limits.set") && <LimitsAction key="limits" d={d} onDone={onDone} />}
          {can("features.set") && <FeaturesAction key="features" d={d} onDone={onDone} />}
        </Group>
        <AccountActions d={d} onDone={onDone} />
      </div>
    </Card>
  );
}
